import { useCallback, useDebugValue } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AsyncEntity } from 'src/global';
import { RootState } from 'src/store';
import { AppDispatch } from 'src/index';
import { useUploadFileToBucket } from 'src/hooks/useUploadFileToBucket';
import { DeviceGroupVideo } from 'src/features/deviceGroupVideos/deviceGroupVideos.types';
import {
  deviceGroupVideosSelectors,
  updateDeviceGroupVideo,
} from 'src/features/deviceGroupVideos/deviceGroupVideos.state';
import { uploadDeviceGroupVideoImage } from 'src/features/deviceGroupVideos/deviceGroupVideos.api';

export const useDeviceGroupVideosState = (): RootState['deviceGroupVideos'] => {
  const state = useSelector<RootState, RootState['deviceGroupVideos']>(state => state.deviceGroupVideos);

  useDebugValue(state);

  return state;
};

export const useDeviceGroupVideos = (): DeviceGroupVideo[] => {
  const banners = useSelector<RootState, RootState['deviceGroupVideos']>(state => state.deviceGroupVideos);

  const result = deviceGroupVideosSelectors.selectAll(banners);

  useDebugValue(result);

  return result;
};

export const useDeviceGroupVideoById = (id: string): AsyncEntity<DeviceGroupVideo> | null => {
  const banners = useSelector<RootState, RootState['deviceGroupVideos']>(state => state.deviceGroupVideos);

  const result = deviceGroupVideosSelectors.selectById(banners, id) || null;

  useDebugValue(result);

  return result;
};

export type UseVideoImage = (params: { videoId: string; groupId: string }) => {
  progress: number;
  uploadVideoImage: (file: File) => void;
};

export const useDeviceGroupVideoImage: UseVideoImage = ({ videoId, groupId }) => {
  const dispatch: AppDispatch = useDispatch();
  const video = useDeviceGroupVideoById(videoId) as DeviceGroupVideo;
  const [progress, uploadFile] = useUploadFileToBucket();

  const updatePackageImage = useCallback(
    async (file: File) => {
      const { name: filename } = file;
      const images = video.image || [];

      const credentials = await uploadDeviceGroupVideoImage({
        videoId,
        groupId,
        data: { filename },
      });

      const { url, fields } = credentials.data;
      const { key } = fields;

      if (credentials.status < 400) {
        const response = await uploadFile({ ...credentials.data, file });

        if (response) {
          console.log([...images, `${url}/${key}`]);
          dispatch(
            updateDeviceGroupVideo({
              groupId: groupId,
              videoId,
              data: { image: `${url}/${key}` },
            })
          );
        }
      }
    },
    [groupId, dispatch, videoId, uploadFile, video]
  );

  return { progress, uploadVideoImage: updatePackageImage };
};
